import { deckShareCrudConfig, type DeckShare } from '@/config/objects/deck-shares';
import { CrudListView, type CrudConfig } from '@startupquickstart/frontend';
import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';

interface DeckSharesListProps {
  /** Optional custom config (with modified endpoints, actions, etc.) */
  config?: CrudConfig<DeckShare>;
  /** Optional default where clause for filtering */
  defaultWhere?: Record<string, unknown>;
  /** Optional custom onItemClick handler */
  onItemClick?: (share: DeckShare) => void;
  className?: string;
  /** Base URL for navigation, defaults to /presentation-shares */
  baseUrl?: string;
  breadcrumbs?: Array<{ label: string; href?: string; current?: boolean }>;
}

/**
 * Reusable presentation-shares list. Pass a custom config with modified actions
 * to customize behavior.
 */
export function DeckSharesList({
  config,
  baseUrl = '/presentation-shares',
  defaultWhere,
  onItemClick,
  className,
  breadcrumbs,
}: DeckSharesListProps) {
  const navigate = useNavigate();

  const handleItemClick = useCallback(
    (share: DeckShare) => {
      if (onItemClick) {
        onItemClick(share);
      } else {
        navigate(`${baseUrl}/${share.id}`);
      }
    },
    [navigate, onItemClick, baseUrl],
  );

  return (
    <div className={className}>
      <CrudListView
        config={config || deckShareCrudConfig}
        baseUrl={baseUrl}
        onItemClick={handleItemClick}
        defaultWhere={defaultWhere}
        breadcrumbs={breadcrumbs}
        getValueFromItem={(share: DeckShare) => share.id}
        getLabelFromItem={(share: DeckShare) => share.recipientName}
      />
    </div>
  );
}
